import React, { useEffect, useRef, useState } from 'react';
import { useStorage } from '../../Utils/Utils';
import EmptyState from './EmptyState';
import UploadingState from './UploadingState';
import UploadedState from './UploadedState';
import ErrorState from './ErrorState';
import ReadonlyState from './ReadonlyState';
import UrlField from './UrlField';
import CropModal from './CropModal';
import LibraryModal from './LibraryModal';
import ModalShell from './ModalShell';
import Viewer from './Viewer';
import './ImageUploadControl.scss';

const ACCEPT = ['image/png', 'image/jpeg', 'image/svg+xml'];
const ACCEPT_ATTR = '.png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml';
const DEFAULT_MAX_BYTES = 2 * 1024 * 1024;
const DEFAULT_W = 696;
const DEFAULT_H = 200;
const LIBRARY_URL = '/funds/image/list';
const UPLOAD_PATH = '/api/funds/image/batch/upload';
const URL_PREFIX = 'url:';

const fileSize = (bytes) => {
	if (bytes == null) return null;
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Overwriting an image keeps the same S3 URL, so the browser would serve the cached (old) bytes.
// Append a version query param to the *display* src (never the stored value) to force a refetch.
const withVersion = (url, version) =>
	url && version ? `${url}${url.indexOf('?') === -1 ? '?' : '&'}v=${encodeURIComponent(version)}` : url;

// A stable key that changes only when the object's content does, so unchanged thumbnails stay cached.
const cacheKey = (row) => (row && (row.lastModified || row.eTag || row.ETag)) || '';

// Reads the per-portfolio constraints sent by the backend (schema.rules.imageOption).
// Falls back to the legacy array shape (imageOptions[0]) so pre-migration configs still work.
// width/height are returned only when the rule declares them; their absence means "keep original"
// (free crop, no enforced banner ratio) rather than the 696×200 banner default.
const readImageOpts = (schema) => {
	const rules = schema && schema.rules;
	let rule = rules && rules.imageOption;
	if (!rule && rules && Array.isArray(rules.imageOptions) && rules.imageOptions.length > 0) {
		rule = rules.imageOptions[0];
	}
	return {
		maxBytes: rule && typeof rule.maxSize === 'number' ? rule.maxSize : DEFAULT_MAX_BYTES,
		width: rule && rule.width > 0 ? rule.width : null,
		height: rule && rule.height > 0 ? rule.height : null,
		rule: rule || null,
	};
};

const nameFromUrl = (url) => {
	if (!url) return 'Logo';
	const parts = url.split('?')[0].split('/');
	return decodeURIComponent(parts[parts.length - 1] || 'Logo');
};

const stripPrefix = (v) => (typeof v === 'string' && v.indexOf(URL_PREFIX) === 0 ? v.substring(URL_PREFIX.length) : v || '');

const libraryToCards = (rows) =>
	(rows || []).map((row, i) => {
		const url = typeof row === 'string' ? row : row.url;
		const version = typeof row === 'string' ? '' : cacheKey(row);
		const size =
			row && row.sizeFormatted
				? row.sizeFormatted
				: row && typeof row.size === 'number'
				? fileSize(row.size)
				: null;
		return {
			id: url || `img-${i}`,
			src: withVersion(url, version),
			url,
			name: nameFromUrl(url),
			file: nameFromUrl(url),
			dims: row && row.dims ? row.dims : null,
			size,
		};
	});

const ImageUploadControl = ({ schema, datum, editMode, disabled, onChange }) => {
	const storage = useStorage();
	const currentUrl = stripPrefix(datum && datum.value);
	const { maxBytes, width, height, rule: imageRule } = readImageOpts(schema);
	const hasDimensions = !!(width && height);
	const targetW = width || DEFAULT_W;
	const targetH = height || DEFAULT_H;
	const maxLabel = fileSize(maxBytes);
	const dimsLabel = hasDimensions ? `${width}×${height}` : null;

	const initialState = !editMode || disabled ? (currentUrl ? 'readonly' : 'empty') : currentUrl ? 'uploaded' : 'empty';

	const [state, setState] = useState(initialState);
	const [logo, setLogo] = useState(
		currentUrl
			? { src: currentUrl, url: currentUrl, name: nameFromUrl(currentUrl), file: nameFromUrl(currentUrl) }
			: null
	);
	const [progress, setProgress] = useState(0);
	const [errMsg, setErrMsg] = useState('');
	const [crop, setCrop] = useState(null);
	const [libraryOpen, setLibraryOpen] = useState(false);
	const [viewer, setViewer] = useState(false);
	const [pendingName, setPendingName] = useState('');
	const [library, setLibrary] = useState([]);
	const [pendingUpload, setPendingUpload] = useState(null);

	const fileInputRef = useRef(null);
	const xhrRef = useRef(null);

	useEffect(() => {
		if (!editMode) return;
		storage
			.getOrFetch(LIBRARY_URL)
			.then((resp) => {
				const rows = (resp && resp.data) || resp || [];
				setLibrary(libraryToCards(rows));
			})
			.catch(() => setLibrary([]));
	}, [editMode, storage]);

	useEffect(() => {
		const next = !editMode || disabled ? (currentUrl ? 'readonly' : 'empty') : currentUrl ? 'uploaded' : 'empty';
		setState(next);
		if (currentUrl && (!logo || logo.url !== currentUrl)) {
			setLogo({ src: currentUrl, url: currentUrl, name: nameFromUrl(currentUrl), file: nameFromUrl(currentUrl) });
		}
		if (!currentUrl && logo) setLogo(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editMode, disabled, currentUrl]);

	useEffect(() => () => xhrRef.current && xhrRef.current.abort(), []);

	const pickFile = () => fileInputRef.current && fileInputRef.current.click();

	const refreshLibrary = () => {
		const promise = storage.refresh ? storage.refresh(LIBRARY_URL) : storage.getOrFetch(LIBRARY_URL);
		promise
			.then((resp) => {
				const rows = (resp && resp.data) || resp || [];
				setLibrary(libraryToCards(rows));
			})
			.catch(() => {});
	};

	const commitLogo = (url, meta = {}) => {
		const next = {
			src: meta.src || url,
			url,
			name: meta.name || nameFromUrl(url),
			file: meta.file || nameFromUrl(url),
			size: meta.size || null,
			dims: meta.dims || null,
		};
		setLogo(next);
		setState('uploaded');
		onChange(URL_PREFIX + url);
	};

	const validate = (file) => {
		if (!ACCEPT.includes(file.type)) {
			const ext = (file.type || '').split('/')[1] || (file.name.split('.').pop() || 'unsupported');
			return `“${file.name}” is a ${ext} file. Use PNG, JPG or SVG.`;
		}
		if (file.size > maxBytes) {
			return `“${file.name}” is ${fileSize(file.size)}. The limit is ${maxLabel} — try compressing it.`;
		}
		return null;
	};

	const uploadBlob = (blob, name, originalFile) => {
		setPendingName(name);
		setProgress(0);
		setState('uploading');
		setErrMsg('');

		const form = new FormData();
		const filename = name || (originalFile && originalFile.name) || 'logo.png';
		form.append('files', blob, filename);
		// Resizing already happened client-side, so tell the backend to store the bytes as-is
		// (the backend always keeps the original name; same name overwrites the existing object).
		form.append('byPassRules', 'true');
		if (imageRule) {
			form.append('rules', JSON.stringify(imageRule));
		}

		const xhr = new XMLHttpRequest();
		xhrRef.current = xhr;
		xhr.open('POST', UPLOAD_PATH, true);
		xhr.withCredentials = true;

		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable) {
				setProgress((e.loaded / e.total) * 100);
			}
		};

		xhr.onload = () => {
			xhrRef.current = null;
			if (xhr.status < 200 || xhr.status >= 300) {
				setErrMsg(`Server responded with ${xhr.status}. Try again or pick from the library.`);
				setState('error');
				return;
			}
			let parsed = null;
			try {
				parsed = JSON.parse(xhr.responseText);
			} catch (e) {
				setErrMsg('Server returned an unexpected response.');
				setState('error');
				return;
			}
			if (parsed && parsed.success === false) {
				setErrMsg(parsed.message || 'Upload failed.');
				setState('error');
				return;
			}
			const list = (parsed && parsed.data) || [];
			const url = list[0];
			if (!url) {
				setErrMsg('Upload succeeded but no URL was returned.');
				setState('error');
				return;
			}
			refreshLibrary();
			commitLogo(url, {
				name: filename.replace(/\.[^.]+$/, ''),
				file: filename,
				size: fileSize(blob.size || (originalFile && originalFile.size)),
				dims: blob.type === 'image/png' || blob.type === 'image/jpeg' ? dimsLabel : null,
				// Force the editor to show the just-uploaded bytes even when the URL is unchanged.
				src: withVersion(url, Date.now()),
			});
		};

		xhr.onerror = () => {
			xhrRef.current = null;
			setErrMsg('Could not reach the server. Check your connection and try again.');
			setState('error');
		};
		xhr.onabort = () => {
			xhrRef.current = null;
		};

		xhr.send(form);
	};

	const libraryHasName = (name, list) => {
		if (!name) return false;
		const target = name.toLowerCase();
		return (list || library).some((item) => (item.file || item.name || '').toLowerCase() === target);
	};

	// Funnels every upload through a same-name check: an existing library image with the same
	// filename would be overwritten, so confirm with the user first. The check reads the authoritative
	// (cached) library list rather than the React state, so it is not defeated by the list still
	// loading or a transient earlier fetch failure that left state empty.
	const startUpload = async (blob, name, originalFile) => {
		let names = library;
		try {
			const resp = await storage.getOrFetch(LIBRARY_URL);
			names = libraryToCards((resp && resp.data) || resp || []);
		} catch (e) {
			// Genuinely can't reach the list — fall back to whatever we already have (best effort).
		}
		if (libraryHasName(name, names)) {
			setPendingUpload({ blob, name, originalFile });
			return;
		}
		uploadBlob(blob, name, originalFile);
	};

	const confirmOverwrite = () => {
		if (!pendingUpload) return;
		const { blob, name, originalFile } = pendingUpload;
		setPendingUpload(null);
		uploadBlob(blob, name, originalFile);
	};

	const cancelOverwrite = () => setPendingUpload(null);

	const handleFiles = (files) => {
		const f = files && files[0];
		if (!f) return;
		const err = validate(f);
		if (err) {
			setErrMsg(err);
			setState('error');
			return;
		}
		if (f.type === 'image/svg+xml') {
			startUpload(f, f.name, f);
			return;
		}
		const reader = new FileReader();
		reader.onload = (e) => setCrop({ src: e.target.result, name: f.name, original: f });
		reader.onerror = () => {
			setErrMsg('Could not read the file. Try a different image.');
			setState('error');
		};
		reader.readAsDataURL(f);
	};

	const onLibraryPick = (item) => {
		setLibraryOpen(false);
		if (!item || !item.url) return;
		commitLogo(item.url, {
			name: item.name,
			file: item.file,
			size: item.size,
			dims: item.dims,
			src: item.src,
		});
	};

	const onUrlApply = (url) => {
		commitLogo(url, { name: nameFromUrl(url), file: nameFromUrl(url) });
	};

	const remove = () => {
		setLogo(null);
		setState('empty');
		onChange('');
	};

	if (!editMode || disabled || state === 'readonly') {
		const hasLogo = !!(logo && logo.src);
		return (
			<div className="image-upload-control">
				{hasLogo ? (
					<ReadonlyState logo={logo} />
				) : (
					<div style={{ color: '#7C8398', fontSize: '0.88rem' }}>No logo set</div>
				)}
			</div>
		);
	}

	return (
		<div className="image-upload-control">
			<input
				ref={fileInputRef}
				type="file"
				accept={ACCEPT_ATTR}
				style={{ display: 'none' }}
				onChange={(e) => {
					handleFiles(e.target.files);
					e.target.value = '';
				}}
				aria-hidden="true"
			/>

			{state === 'empty' && (
				<EmptyState
					libraryCount={library.length}
					maxLabel={maxLabel}
					dimsLabel={dimsLabel}
					onPickFile={pickFile}
					onOpenLibrary={() => setLibraryOpen(true)}
					onFilesDropped={handleFiles}
				/>
			)}
			{state === 'uploading' && <UploadingState progress={progress} fileName={pendingName} />}
			{state === 'uploaded' && logo && (
				<UploadedState
					logo={logo}
					onReplace={() => setLibraryOpen(true)}
					onRemove={remove}
					onView={() => setViewer(true)}
				/>
			)}
			{state === 'error' && (
				<ErrorState
					message={errMsg || 'Something went wrong. Please try again.'}
					onRetry={pickFile}
					onLibrary={() => {
						setState('empty');
						setLibraryOpen(true);
					}}
				/>
			)}

			<UrlField url={state === 'uploaded' && logo ? logo.url : ''} onApply={onUrlApply} />

			{libraryOpen && (
				<LibraryModal
					images={library}
					onClose={() => setLibraryOpen(false)}
					onSelect={onLibraryPick}
					onUploadNew={() => {
						setLibraryOpen(false);
						pickFile();
					}}
				/>
			)}
			{crop && (
				<CropModal
					src={crop.src}
					fileName={crop.name}
					targetW={targetW}
					targetH={targetH}
					mimeType={crop.original && crop.original.type}
					alignment={hasDimensions ? imageRule && imageRule.alignment : undefined}
					maxLabel={maxLabel}
					freeCrop={!hasDimensions}
					onCancel={() => setCrop(null)}
					onConfirm={(blob) => {
						const orig = crop.original;
						const name = crop.name;
						setCrop(null);
						if (blob) {
							startUpload(blob, name, orig);
						} else if (orig) {
							startUpload(orig, name, orig);
						} else {
							setErrMsg('Could not export the cropped image.');
							setState('error');
						}
					}}
				/>
			)}
			{viewer && logo && <Viewer logo={logo} dimsLabel={dimsLabel} onClose={() => setViewer(false)} />}
			{pendingUpload && (
				<ModalShell onClose={cancelOverwrite} ariaLabel="Replace existing image" style={{ maxWidth: 440 }}>
					<div className="image-upload-modal__header">
						<div style={{ flex: 1 }}>
							<div className="image-upload-modal__title">Replace existing image?</div>
							<div className="image-upload-modal__subtitle">
								An image named “{pendingUpload.name}” already exists and will be overwritten.
							</div>
						</div>
					</div>
					<div className="image-upload-modal__footer" style={{ justifyContent: 'flex-end' }}>
						<button type="button" className="image-upload-modal__btn-secondary" onClick={cancelOverwrite}>
							Cancel
						</button>
						<button type="button" className="image-upload-modal__btn-primary" onClick={confirmOverwrite}>
							Replace
						</button>
					</div>
				</ModalShell>
			)}
		</div>
	);
};

export default ImageUploadControl;
